import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { PlusIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DropIndicator, type Key, useDragAndDrop } from "react-aria-components";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useListData } from "react-stately";
import type { z } from "zod";

import { testSpec } from "@blis/shared/schemas";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  Description,
  FieldError,
  FieldGroup,
  Input,
  Label,
  TextArea,
  TextField,
  UnstyledInput,
} from "@/components/ui/field";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Menu, MenuItem, MenuTrigger } from "@/components/ui/menu";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { useConfig } from "@/lib/config";
import { mutationResponse } from "@/utils/trpc";
import type { ProcessTemplate } from "../../types";
import { sanitizeHtmlToFragment } from "../../utils/html";
import { AdminForm } from "../AdminForm";
import { Section } from "../Section";
import { TabLayout } from "../TabLayout";
import { ArchivedBadge, isTubeArchived } from "../tubes/ArchivedBadge";

type TestSpec = RouterOutputs["admin"]["testSpec"]["get"];

const schema = testSpec.create;

type FormValues = z.infer<typeof schema>;

const typeValues = [
  { label: "Serology", value: "SEROLOGY" },
  { label: "Clinical", value: "CLINICAL" },
  { label: "HLA", value: "HLA" },
  { label: "Microbiology", value: "MICROBIOLOGY" },
];

export function TestSpecDetailForm({ test }: { test?: TestSpec }) {
  const { data, isLoading } = trpc.admin.testSpec.options.useQuery();
  const createMutation = trpc.admin.testSpec.create.useMutation();
  const updateMutation = trpc.admin.testSpec.update.useMutation();

  const navigate = useNavigate();

  const { control, formState, ...methods } = useForm({
    defaultValues: {
      ...test,
      price: test?.price?.toNumber(),
      billingMode: test?.billingMode || "PER_ACCESSION",
      sampleAssignmentType: test?.sampleAssignmentType || "AUTO_ASSIGN",
    },
    resolver: zodResolver(schema),
  });

  const config = useConfig();

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (test) {
      updateMutation.mutate(
        {
          id: test.id,
          ...values,
        },
        mutationResponse({
          errorTitle: "Error updating test spec",
          successMessage: "Test spec updated",
        }),
      );
    } else {
      createMutation.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding test spec",
          successMessage: "Test spec added",
          onSuccess(data) {
            if (data) {
              navigate({
                to: "/admin/tests/$id/edit",
                params: { id: data.id },
              });
            }
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <FormProvider control={control} formState={formState} {...methods}>
        <AdminForm onSubmit={methods.handleSubmit(onSubmit)}>
          <TextInputs />
          <TypeSelect />
          <div className="flex flex-col gap-2">
            {config.enableSubjectSampleMigration ? (
              <ContainerPreferences
                testSpec={test}
                containers={data?.tubes || []}
                fetching={isLoading}
              />
            ) : (
              <PreferredTubeSelect
                tubes={data?.tubes || []}
                fetching={isLoading}
              />
            )}
            <ReferenceLabSelect labs={data?.labs || []} fetching={isLoading} />
            <InstrumentSelect
              instruments={data?.instruments || []}
              fetching={isLoading}
            />
            <InvoiceOverrideSelect
              orgs={data?.orgs || []}
              fetching={isLoading}
            />
            <ProcessSelect />
            <GroupSelect />
            <SampleAssignmentTypeSelect />
            <BillingModeSelect />
          </div>
          <TestSpecOptions />
          <TestSpecNotes />
          <ReportDisclaimer />
          <Separator />
          <div className="flex justify-end gap-2">
            <Link to="/admin/tests" variant="outline" asButton>
              Cancel
            </Link>
            <Button
              type="submit"
              isPending={updateMutation.isPending || createMutation.isPending}
            >
              Save
            </Button>
          </div>
        </AdminForm>
      </FormProvider>
    </TabLayout>
  );
}

function TextInputs() {
  const { control } = useFormContext<FormValues>();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="name"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Name</Label>
            <Input />
            <Description>Display name for the test.</Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="code"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="code"
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Code</Label>
            <Input />
            <Description>
              An arbitrary alphanumeric code for the test. This should match
              codes on instruments.
            </Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="manufacturer"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="manufacturer"
            {...field}
            value={field.value || ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Manufacturer</Label>
            <Input />
            <Description>The manufacturer of the test kit.</Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="kitName"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="kit-name"
            {...field}
            value={field.value || ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Kit name</Label>
            <Input />
            <Description>The name of the test kit.</Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        name="price"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            type="number"
            id="price"
            {...field}
            value={field.value?.toString() ?? ""}
            onChange={(e) => field.onChange(e)}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Price</Label>
            <FieldGroup className="px-2">
              <Text muted>$</Text>
              <UnstyledInput placeholder="0.00" />
              <Text muted>USD</Text>
            </FieldGroup>
            <Description>Price in USD.</Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
    </div>
  );
}

function TypeSelect() {
  const { control } = useFormContext<FormValues>();

  return (
    <Controller
      control={control}
      name="type"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Type</Label>
          <SelectTrigger />
          <SelectList items={typeValues}>
            {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function ContainerPreferences({
  containers,
  fetching,
  testSpec,
}: {
  testSpec?: TestSpec;
  containers: { id: number; name: string }[];
  fetching: boolean;
}) {
  const { control, setValue } = useFormContext<FormValues>();
  const prefs = testSpec?.containerPreferences || [];

  const list = useListData({
    initialItems: (prefs || []).map((p) => ({
      id: p.container.id,
      container: {
        id: p.container.id,
        name: p.container.name,
        archivedAt: p.container.archivedAt,
      },
      priority: p.priority,
    })),
  });
  const dropdownOptions = containers.filter(
    (c) => !list.items.some((p) => p.container.id === c.id),
  );
  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys) =>
      [...keys].map((key) => ({
        "text/plain": list.getItem(key)?.container.name ?? "",
      })),
    onReorder(e) {
      if (e.target.dropPosition === "before") {
        list.moveBefore(e.target.key, e.keys);
      } else if (e.target.dropPosition === "after") {
        list.moveAfter(e.target.key, e.keys);
      }
    },
    renderDropIndicator(target) {
      return <DropIndicator target={target} className="bg-border w-full h-1" />;
    },
  });
  const onAction = (action: Key) => {
    const container = dropdownOptions.find((c) => c.id === Number(action));
    if (container) {
      list.append({
        id: container.id,
        container: {
          id: container.id,
          name: container.name,
          archivedAt: null,
        },
        priority: list.items.length + 1,
      });
    }
  };
  useEffect(() => {
    setValue(
      "containerPreferences",
      list.items.map((p, idx) => ({
        containerId: p.container.id,
        priority: idx + 1,
      })),
    );
  }, [list.items, setValue]);

  return (
    <Controller
      control={control}
      name="containerPreferences"
      render={() => (
        <div className="flex flex-col gap-2">
          <Label>Container preferences</Label>
          <GridList
            aria-label="Container preferences"
            items={list.items}
            renderEmptyState={() => (
              <Text muted size="sm" className="p-3">
                No preferences
              </Text>
            )}
            dragAndDropHooks={dragAndDropHooks}
          >
            {(pref) => (
              <GridListItem id={pref.id}>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {pref.container.name}
                    {isTubeArchived(pref.container) ? <ArchivedBadge /> : null}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onPress={() => list.remove(pref.id)}
                  >
                    <XIcon />
                  </Button>
                </div>
              </GridListItem>
            )}
          </GridList>
          <div>
            <MenuTrigger>
              <Button variant="ghost">
                <PlusIcon />
                Add container preference
              </Button>
              <Menu
                items={dropdownOptions}
                onAction={onAction}
                className="min-w-(--trigger-width)"
              >
                {(item) => <MenuItem id={item.id}>{item.name}</MenuItem>}
              </Menu>
            </MenuTrigger>
          </div>
        </div>
      )}
    />
  );
}

function PreferredTubeSelect({
  tubes,
  fetching,
}: {
  tubes: { id: number; name: string }[];
  fetching: boolean;
}) {
  const { control } = useFormContext<FormValues>();

  return (
    <Controller
      control={control}
      name="preferredTubeId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Preferred tube type</Label>
          <SelectTrigger isClearable />

          <SelectList items={tubes}>
            {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
          </SelectList>
          <Description>
            The selected tube type will be auto-selected during result entry, if
            available.
          </Description>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function ReferenceLabSelect({
  labs,
  fetching,
}: {
  labs: { id: number; name: string; isPrimary?: boolean }[];
  fetching: boolean;
}) {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="referenceOrgId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Reference lab</Label>
          <SelectTrigger isClearable />

          <SelectList items={labs.filter((lab) => !lab.isPrimary)}>
            {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
          </SelectList>
          <Description>
            The reference lab for this test. This will be displayed on reports.
          </Description>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function InstrumentSelect({
  instruments,
  fetching,
}: {
  instruments: { id: number; manufacturer: string; model: string }[];
  fetching: boolean;
}) {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="instrumentId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Instrument</Label>
          <SelectTrigger isClearable />

          <SelectList items={instruments}>
            {(item) => (
              <SelectItem id={item.id}>
                {item.manufacturer} {item.model}
              </SelectItem>
            )}
          </SelectList>
          <Description>The instrument used to run this test.</Description>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function InvoiceOverrideSelect({
  orgs,
  fetching,
}: {
  orgs: { id: number; name: string }[];
  fetching: boolean;
}) {
  const { control } = useFormContext<FormValues>();

  return (
    <Controller
      control={control}
      name="invoiceOverrideId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Invoice override client</Label>
          <SelectTrigger isClearable />

          <SelectList items={orgs}>
            {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
          </SelectList>
          <Description>
            If set, this test will be invoiced to the selected client instead of
            the client that ordered the test.
          </Description>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function ProcessSelect() {
  const [processTemplate, setProcessTemplate] =
    useState<ProcessTemplate | null>(null);
  const { control, watch } = useFormContext<FormValues>();

  const processTemplateVersionId = watch("processTemplateVersionId");
  const { data } = trpc.admin.process.listTemplates.useQuery();
  const processes = data || [];

  useEffect(() => {
    if (processTemplateVersionId) {
      const processTemplate = processes.find((p) =>
        p.versions.some((v) => v.id === processTemplateVersionId),
      );
      if (processTemplate) {
        setProcessTemplate(processTemplate);
      }
    }
  }, [processTemplateVersionId, processes]);

  return (
    <div className="flex items-center gap-2">
      <Select
        selectedKey={processTemplate?.id ?? null}
        onSelectionChange={(key) => {
          const processTemplate = processes.find((p) => p.id === key);
          if (processTemplate) {
            setProcessTemplate(processTemplate);
          }
        }}
        className="flex-1"
      >
        <Label>Process template</Label>
        <SelectTrigger />
        <SelectList items={processes}>
          {(item) => (
            <SelectItem id={item.id} textValue={item.name}>
              {item.name}
            </SelectItem>
          )}
        </SelectList>
      </Select>
      <Controller
        control={control}
        name="processTemplateVersionId"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
            className="flex-1"
            isDisabled={!processTemplate}
          >
            <Label>Template version</Label>
            <SelectTrigger />
            <SelectList items={processTemplate?.versions ?? []}>
              {(item) => (
                <SelectItem id={item.id} textValue={item.version.toString()}>
                  v{item.version}
                </SelectItem>
              )}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </div>
  );
}

function GroupSelect() {
  const { control, watch } = useFormContext<FormValues>();
  const { data } = trpc.admin.testSpecGroup.list.useQuery();
  const groups = data || [];

  const type = watch("type");

  if (type !== "CLINICAL") {
    return null;
  }

  return (
    <Controller
      control={control}
      name="testSpecGroupId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Group</Label>
          <SelectTrigger isClearable />

          <SelectList items={groups}>
            {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
          </SelectList>
          <Description>
            If set, this test will be grouped with other tests in the same group
            on reports. (Clinical tests only)
          </Description>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

const sampleAssignmentTypeOptions = [
  {
    label: "Auto assign",
    value: "AUTO_ASSIGN",
    description: "The test will be assigned to the highest priority container.",
  },
  {
    label: "Manual assign",
    value: "MANUAL_ASSIGN",
    description:
      "The test will need to be assigned to the containers manually.",
  },
];

function SampleAssignmentTypeSelect() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="sampleAssignmentType"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Sample assignment type</Label>
          <SelectTrigger isClearable />
          <SelectList items={sampleAssignmentTypeOptions}>
            {(item) => (
              <SelectItem id={item.value} textValue={item.label}>
                <Text slot="label">{item.label}</Text>
                <Text slot="description">{item.description}</Text>
              </SelectItem>
            )}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

const billingModeOptions = [
  {
    label: "Bill per accession",
    value: "PER_ACCESSION",
  },
  {
    label: "Bill per sample",
    value: "PER_SAMPLE",
  },
];

function BillingModeSelect() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="billingMode"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Billing mode</Label>
          <SelectTrigger isClearable />
          <SelectList items={billingModeOptions}>
            {(item) => (
              <SelectItem id={item.value} textValue={item.label}>
                {item.label}
              </SelectItem>
            )}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function TestSpecOptions() {
  const { control } = useFormContext<FormValues>();
  return (
    <div className="flex flex-col gap-y-2 relative">
      <Section.Subtitle>Options</Section.Subtitle>
      <Controller
        control={control}
        name="selectable"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-selectable"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Selectable
            </Checkbox>
            <Description>Test can be selected during order entry</Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="printLabels"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-print-labels"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Print labels with worklist
            </Checkbox>
            <Description>Print sample labels with the worklist</Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="printLabelsWithReflex"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-print-labels"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Print labels when reflexed
            </Checkbox>
            <Description>Print sample labels when reflexed</Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="disableTestRun"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="disable-report"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Disable test run
            </Checkbox>
            <Description>
              A test run is not required for this test, and will be disabled
            </Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="disableReport"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="disable-report"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Disable report
            </Checkbox>
            <Description>Reporting is not required for this test</Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="shouldReportReflex"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-report-reflex"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Report reflex result
            </Checkbox>
            <Description>
              If this test is a reflex, then show result in final report
            </Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="alwaysShowHeader"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="always-show-header"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Always display header
            </Checkbox>
            <Description>
              Always display the test name header, even if there is only one
              result spec
            </Description>
          </CheckboxField>
        )}
      />
    </div>
  );
}

function TestSpecNotes() {
  const { control } = useFormContext<FormValues>();

  return (
    <Controller
      name="notes"
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <TextField
          {...field}
          id="test-notes"
          value={field.value || ""}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Notes</Label>
          <TextArea />
          <Description>
            These notes will appear on the report on the same row as the test
            results (Serology reports only)
          </Description>
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function ReportDisclaimer() {
  const { setValue, getValues } = useFormContext<FormValues>();
  const editorRef = useRef<HTMLDivElement>(null);
  const squireRef = useRef<import("squire-rte").default | null>(null);

  useEffect(() => {
    if (!editorRef.current || squireRef.current) return;

    let cancelled = false;

    import("squire-rte").then(({ default: Squire }) => {
      if (cancelled || !editorRef.current) return;

      const editor = new Squire(editorRef.current, {
        sanitizeToDOMFragment: sanitizeHtmlToFragment,
      });
      squireRef.current = editor;

      const initialContent = getValues("reportDisclaimer");
      if (initialContent) {
        editor.setHTML(initialContent);
      }

      editor.addEventListener("input", () => {
        const html = editor.getHTML();
        const isEmpty = html === "<div><br></div>" || html.trim() === "";
        setValue("reportDisclaimer", isEmpty ? null : html);
      });
    });

    return () => {
      cancelled = true;
      squireRef.current?.destroy();
      squireRef.current = null;
    };
  }, [getValues, setValue]);

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="report-disclaimer">Report disclaimer</Label>
      <div
        ref={editorRef}
        className="outline-hidden px-3 py-2 border rounded-lg min-h-[80px] max-h-52 overflow-auto text-sm focus-within:border-ring focus-within:outline-hidden focus-within:ring-[3px] focus-within:ring-ring/20 transition-shadow"
      />
      <Description>
        Disclaimer added to the bottom of reports. Formatting is supported.
      </Description>
    </div>
  );
}
